import { LoadingButton } from '@mui/lab';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAresSearch } from '../hooks/useAresSearch';
import { notify } from '../utils/notification';
import { getAddressFromAresResult } from '../utils/transform';

export interface AresLookupResult {
  name?: string;
  taxId?: string;
  address?: string;
}

interface AresLookupButtonProps {
  getVatId: () => string;
  onLookupSuccess: (result: AresLookupResult) => void;
  variant?: 'text' | 'outlined' | 'contained';
  fullWidth?: boolean;
  disabled?: boolean;
}

export const AresLookupButton = ({
  getVatId,
  disabled,
  onLookupSuccess,
  variant = 'outlined',
  fullWidth = true,
}: AresLookupButtonProps) => {
  const { t } = useTranslation();
  const { search, state } = useAresSearch();

  const loadVendorFromAres = useCallback(
    async (vatId: string): Promise<AresLookupResult | null> => {
      const notifyId = notify.neutral({
        message: t('vendorAresSearch.loading'),
      });
      const aresResult = await search({ vatId: vatId });

      notify.remove(notifyId);

      if (!aresResult.queryAres) {
        notify.warning({ message: t('vendorAresSearch.error') });
        return null;
      }

      notify.positive({ message: t('vendorAresSearch.success') });
      return {
        name: aresResult.queryAres.name || undefined,
        taxId: aresResult.queryAres.taxId || undefined,
        address: getAddressFromAresResult(aresResult.queryAres),
      };
    },
    [search, t],
  );

  const handleAresSearch = useCallback(async () => {
    const vatId = getVatId();
    if (!vatId) {
      return;
    }

    // try look-up in ARES
    const aresLookup = await loadVendorFromAres(vatId);

    if (aresLookup) {
      onLookupSuccess(aresLookup);
    }
  }, [getVatId, loadVendorFromAres, onLookupSuccess]);

  return (
    <LoadingButton
      fullWidth={fullWidth}
      variant={variant}
      loading={state.isPending}
      onClick={handleAresSearch}
      disabled={disabled}
    >
      {t('loadFromAres')}
    </LoadingButton>
  );
};
